import { driver, type DriveStep, type Driver } from "driver.js";

export type { Driver };
/** A driver.js step; re-exported so callers import nothing from driver.js. */
export type TourStep = DriveStep;

export interface StartTourOptions {
  /** The tour's storage-key suffix, e.g. "member-dashboard"; one per tour. */
  id: string;
  steps: TourStep[];
  /** Footer button labels; an SPA passes its own strings. The defaults are English. */
  labels?: { next?: string; prev?: string; done?: string };
  /** Skip the browser-wide seen flag for session-scoped or shared-terminal tours. */
  remember?: boolean;
  /** Extra skin class alongside the shared DearBill popover class. */
  popoverClass?: string;
  /** Keep highlighted controls inactive and keyboard focus inside the explanation. */
  readOnly?: boolean;
  /** Called once after closing, including an immediate close or API destroy. */
  onClosed?: () => void;
}

/** The `localStorage` key of a tour's seen flag. */
function storageKey(id: string): string {
  return `dearbill.tour.${id}`;
}

/**
 * Whether the tour with this id has run in this browser.
 *
 * ponytail: the seen flag lives in this browser's `localStorage` only; the
 * same user on another device, or after clearing site data, sees the tour
 * again. Storage can throw in privacy modes and sandboxed iframes, so a throw
 * reads as unseen and never reaches the page.
 */
export function hasSeenTour(id: string): boolean {
  try {
    return localStorage.getItem(storageKey(id)) !== null;
  } catch {
    return false;
  }
}

export function markTourSeen(id: string): void {
  try {
    localStorage.setItem(storageKey(id), "1");
  } catch {
    // Blocked storage: the tour shows again on the next visit.
  }
}

export function resetTour(id: string): void {
  try {
    localStorage.removeItem(storageKey(id));
  } catch {
    // Blocked storage holds no flag to clear.
  }
}

/**
 * Starts a driver.js tour in the DearBill skin (`.dearbill-tour` in
 * `styles/styles.css`) and returns the running `Driver`.
 *
 * Steps target elements by a `data-tour="<name>"` attribute
 * (`element: '[data-tour="plan-card"]'`), never by a Tailwind class name, so
 * a restyle cannot silently break a tour. With the default `remember: true`,
 * starting marks the tour seen, including a dismissal or navigation away;
 * callers use `hasSeenTour(id)` to decide whether to offer it. Session-scoped
 * tours use `remember: false` and manage their welcome in memory. The flag
 * is set here rather than in an exit hook because driver.js runs `onDestroyed`
 * only once a step's highlight animation has finished, so a close during the
 * first few hundred milliseconds never reaches it.
 */
export function startTour({
  id,
  steps,
  labels,
  remember = true,
  popoverClass,
  readOnly = false,
  onClosed,
}: StartTourOptions): Driver {
  const reducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let closed = false;
  let panel: HTMLElement | undefined;
  const buttons = (): HTMLButtonElement[] =>
    Array.from(panel?.querySelectorAll<HTMLButtonElement>("button:not([disabled])") ?? []).filter(
      (button) =>
        button.style.display !== "none" && button.getAttribute("aria-disabled") !== "true",
    );
  const containFocus = (event: FocusEvent): void => {
    if (!closed && panel && event.target instanceof Node && !panel.contains(event.target)) {
      (buttons()[0] ?? panel).focus();
    }
  };
  const containKeyboard = (event: KeyboardEvent): void => {
    if (closed) return;
    if (!panel) {
      if (["Tab", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    if (event.key === "Tab") {
      const controls = buttons();
      const current = controls.findIndex((button) => button === document.activeElement);
      const next = event.shiftKey
        ? (current - 1 + controls.length) % controls.length
        : (current + 1) % controls.length;
      event.preventDefault();
      event.stopPropagation();
      (controls[next] ?? panel).focus();
    } else if (
      (event.key === "Enter" || event.key === " ") &&
      event.target instanceof Node &&
      !panel.contains(event.target)
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const pointerEvents = ["pointerdown", "mousedown", "pointerup", "mouseup", "click"] as const;
  const containPointer = (event: Event): void => {
    if (closed || !(event.target instanceof Element)) return;
    if (panel?.contains(event.target) || event.target.closest(".driver-overlay")) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };
  const removeGuards = (): void => {
    if (!readOnly) return;
    document.removeEventListener("focusin", containFocus, true);
    document.removeEventListener("keydown", containKeyboard, true);
    for (const event of pointerEvents) document.removeEventListener(event, containPointer, true);
  };
  const finish = (): void => {
    if (closed) return;
    closed = true;
    removeGuards();
    onClosed?.();
  };
  const lifecycle =
    readOnly || onClosed
      ? {
          onDestroyStarted: (): void => tour.destroy(),
          onDestroyed: finish,
        }
      : {};
  const tour = driver({
    popoverClass: ["dearbill-tour", popoverClass].filter(Boolean).join(" "),
    showProgress: true,
    allowKeyboardControl: true,
    animate: !reducedMotion,
    smoothScroll: !reducedMotion,
    disableActiveInteraction: readOnly,
    skipMissingElement: readOnly,
    closeBtnLabel: "Close tour",
    ...lifecycle,
    ...(readOnly
      ? {
          onPopoverRender: (popover: { wrapper: HTMLElement }): void => {
            panel = popover.wrapper;
            panel.setAttribute("aria-modal", "true");
          },
        }
      : {}),
    // Midnight (`--color-midnight` in theme.css) at the Modal backdrop's `/30`.
    overlayColor: "#091c2e",
    overlayOpacity: 0.3,
    nextBtnText: labels?.next ?? "Next",
    prevBtnText: labels?.prev ?? "Back",
    doneBtnText: labels?.done ?? "Done",
    steps,
  });
  if (readOnly || onClosed) {
    const destroy = tour.destroy;
    tour.destroy = (): void => {
      // driver.js can omit onDestroyed while a highlight is still animating.
      // Remove focus guards before its own focus restoration runs.
      removeGuards();
      destroy();
      finish();
    };
  }
  if (remember) markTourSeen(id);
  if (readOnly) {
    document.addEventListener("focusin", containFocus, true);
    document.addEventListener("keydown", containKeyboard, true);
    for (const event of pointerEvents) document.addEventListener(event, containPointer, true);
  }
  tour.drive();
  return tour;
}
